<template>
  <section v-if="contactability" class="contactability" :class="`contactability--${cardMode}`">
    <div class="contactability__row-top">
      <div>
        <div class="contactability__label">Lead time zone</div>
        <div class="contactability__gmt">{{ gmtOffset }}</div>
      </div>
      <span class="contactability__country">
        <span v-if="countryCode" :class="`fflag ff-md fflag-${countryCode}`"></span>
        {{ countryCode || countryName || 'Unknown' }}
      </span>
    </div>

    <div class="contactability__row-bottom">
      <div class="contactability__clock-wrap">
        <span class="contactability__clock">{{ localTime }}</span>
        <span class="contactability__date">{{ localDate }}</span>
      </div>
      <div class="contactability__right">
        <span class="contactability__status">{{ statusEmoji }} {{ label }}</span>
        <div class="contactability__channels">
          <span class="contactability__channel" :class="{ 'contactability__channel--disabled': !contactability.calls_allowed }">
            <img src="@/assets/img/phone-icon.svg" alt="">
            <span>Calls</span>
            <b>{{ contactability.calls_allowed ? '✓' : '×' }}</b>
          </span>
          <span class="contactability__channel" :class="{ 'contactability__channel--disabled': !contactability.whatsapp_allowed }">
            <img src="@/assets/img/wa-icon.svg" alt="">
            <span>WhatsApp</span>
            <b>{{ contactability.whatsapp_allowed ? '✓' : '×' }}</b>
          </span>
        </div>
      </div>
    </div>

    <div v-if="holiday" class="contactability__holiday">🎉 {{ holiday }}</div>
  </section>
</template>

<script>
import { format, parseISO } from 'date-fns'

export default {
  name: 'LeadsDetailContactability',
  props: {
    contactability: Object,
  },
  data() {
    return {
      now: Date.now(),
      timer: null,
    }
  },
  computed: {
    cardMode() {
      const mode = this.contactability.mode
      return mode === 'weekend' ? 'care' : (['ok', 'quiet', 'holiday'].includes(mode) ? mode : 'quiet')
    },
    label() {
      const labels = {
        ok: 'Great time to reach out',
        quiet: 'Quiet hours · email only',
        holiday: 'Holiday · call with care',
        weekend: 'Weekend · call with care',
        unknown: 'Contact time unknown',
      }
      return this.contactability.label || labels[this.contactability.mode] || labels.unknown
    },
    statusEmoji() {
      const emojis = {
        ok: '☀️',
        quiet: '🌙',
        holiday: '🎉',
        weekend: '☀️',
      }
      return emojis[this.contactability.mode] || '☀️'
    },
    countryCode() {
      return this.contactability.country && this.contactability.country.code ? this.contactability.country.code : ''
    },
    countryName() {
      return this.contactability.country && this.contactability.country.name ? this.contactability.country.name : ''
    },
    gmtOffset() {
      const offset = this.contactability.timezone && this.contactability.timezone.gmt_offset
      if (!offset) return 'GMT —'
      const [hours, minutes] = offset.split(':')
      const normalizedHours = Number(hours)
      const sign = normalizedHours > 0 ? '+' : ''
      return minutes === '00' ? `GMT ${sign}${normalizedHours}` : `GMT ${offset}`
    },
    localTime() {
      const timezone = this.contactability.timezone && this.contactability.timezone.name
      if (!timezone) return this.contactability.local_datetime ? this.contactability.local_datetime.slice(11, 16) : '—'

      return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone,
      }).format(this.now)
    },
    localDate() {
      if (this.contactability.local_date) return format(parseISO(this.contactability.local_date), 'EEEE, d MMMM')
      return this.contactability.local_weekday || '—'
    },
    holiday() {
      const holidays = this.contactability.holidays || []
      if (!holidays.length) return ''
      const firstHoliday = holidays[0]
      const holidayName = typeof firstHoliday === 'string' ? firstHoliday : firstHoliday.name
      if (!holidayName) return ''
      const suffix = holidays.length > 1 ? ` +${holidays.length - 1} more` : ''
      return `${holidayName}${suffix} · public holiday in ${this.countryName || this.countryCode}`
    },
  },
  watch: {
    'contactability.timezone.name': {
      immediate: true,
      handler() {
        this.stopClock()
        if (this.contactability && this.contactability.timezone && this.contactability.timezone.name) {
          this.timer = setInterval(() => { this.now = Date.now() }, 30000)
        }
      }
    }
  },
  beforeDestroy() {
    this.stopClock()
  },
  methods: {
    stopClock() {
      if (this.timer) clearInterval(this.timer)
      this.timer = null
    },
  },
}
</script>

<style lang="sass" scoped>
.contactability
  margin: 0
  margin-left: auto
  max-width: 540px
  padding: 16px 20px
  border: 0
  border-radius: 14px
  color: #fff
  display: flex
  flex-direction: column

.contactability--ok
  background: linear-gradient(120deg, #256b44, #4e9e63)

.contactability--care
  background: linear-gradient(120deg, #c77e1e, #e8b33c)

.contactability--quiet
  background: linear-gradient(120deg, #333f5c, #55658e)

.contactability--holiday
  background: linear-gradient(120deg, #5d4a9c, #8a6fc9)

.contactability__row-top
  display: flex
  align-items: flex-start
  justify-content: space-between
  gap: 12px

.contactability__label
  font-size: 12.5px
  font-weight: 700
  letter-spacing: 1px
  opacity: .9
  text-transform: uppercase

.contactability__gmt
  margin-top: 2px
  font-size: 27px
  font-weight: 800
  letter-spacing: .3px

.contactability__country
  display: inline-flex
  align-items: center
  gap: 7px
  padding: 6px 14px
  border-radius: 20px
  background: rgba(255, 255, 255, .22)
  font-size: 14.5px
  font-weight: 600
  white-space: nowrap

.contactability__country .fflag
  width: 20px
  height: 14px

.contactability__row-bottom
  display: flex
  align-items: flex-end
  justify-content: space-between
  gap: 12px
  margin-top: 14px

.contactability__clock-wrap
  display: flex
  align-items: baseline
  gap: 10px
  min-width: 0

.contactability__clock
  font-size: 38px
  font-weight: 800
  letter-spacing: .5px
  line-height: 1

.contactability__date
  font-size: 14.5px
  opacity: .95
  white-space: nowrap

.contactability__right
  display: flex
  flex-direction: column
  align-items: flex-end
  gap: 7px
  flex-shrink: 0

.contactability__status
  padding: 8px 16px
  border-radius: 20px
  font-size: 13.5px
  font-weight: 700
  white-space: nowrap

.contactability--ok .contactability__status
  color: #1f6b3a
  background: #e4f3e6

.contactability--care .contactability__status
  color: #8a5a00
  background: #fff3d6

.contactability--quiet .contactability__status
  color: #fff
  background: rgba(255, 255, 255, .16)

.contactability--holiday .contactability__status
  color: #4b3583
  background: #efe9fb

.contactability__channels
  display: flex
  gap: 6px

.contactability__channel
  display: inline-flex
  align-items: center
  gap: 5px
  padding: 3px 10px
  border-radius: 16px
  background: rgba(255, 255, 255, .18)
  font-size: 11.5px
  font-weight: 600

  img
    width: 12px
    height: 12px
    filter: brightness(0) invert(1)

  b
    font-size: 12px

.contactability__channel--disabled
  opacity: .7

  span
    text-decoration: line-through
    text-decoration-thickness: 1.5px

.contactability__holiday
  display: flex
  align-items: center
  gap: 7px
  margin-top: 10px
  padding-top: 9px
  border-top: 1px solid rgba(255, 255, 255, .18)
  font-size: 13px
  font-weight: 600
  opacity: .95

@media(max-width: 480px)
  .contactability
    width: calc(100vw - 40px)
    max-width: calc(100vw - 40px)
    min-height: unset
    padding: 15px
    margin-left: 0

  .contactability__row-top, .contactability__row-bottom
    flex-wrap: wrap

  .contactability__right
    align-items: flex-start

  .contactability__clock-wrap
    flex-wrap: wrap
</style>
